/**
 * The floating chat window header shared by the canvas and home/workspace
 * surfaces: back button, session-switcher dropdown (current name + compact
 * relative timestamp; switch/delete rows; "New chat" footer), and the
 * right-hand action cluster (new chat, surface-specific extras, windowed ↔
 * full-height size toggle, minimize-to-FAB).
 *
 * Surface flavor comes in through props/slots — the normalized session list
 * and titles, the canvas's controlled dropdown + create-pending spinner, the
 * workspace's open-as-tab action (`extraActions`). The DOM is the single
 * source of truth for both surfaces, so header chrome can't drift.
 */

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Loader2,
  Maximize2,
  MessagesSquare,
  Minimize2,
  Minus,
  Plus,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";

/** Compact relative time for the inline header timestamp — "now", "1m ago", "3h ago", … */
function shortRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return "";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 45) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

/** One row in the session-switcher dropdown (surface lists normalize to this). */
export interface FloatingChatHeaderSession {
  /** React key — the listing row's own id (may differ from the session id). */
  key: string;
  /** Session id passed to switch/delete. */
  id: string;
  /** Display name, fallback already applied by the surface. */
  name: string;
  /** ISO timestamp for the row's relative "x ago". */
  updatedAt: string;
}

export interface FloatingChatHeaderProps {
  /** Trigger title (current session name, or the surface's fallback). */
  title: string;
  /** Current session's ISO timestamp for the inline "x ago" beside the title. */
  updatedAt?: string;
  /** Sessions listed in the switcher; empty renders a plain (non-dropdown) title. */
  sessions: FloatingChatHeaderSession[];
  /** Session highlighted with a check in the switcher. */
  currentSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  /** Per-surface delete aria-label ("Delete chat" / "Delete session"). */
  deleteSessionAriaLabel: string;
  /** Start a new chat (dropdown footer + the header's Plus button). */
  onNewChat: () => void;
  /** Swap the Plus for a spinner while a create is in flight (canvas). */
  newChatPending?: boolean;
  newChatDisabled?: boolean;
  canGoBack: boolean;
  onGoBack: () => void;
  /** True in the full-height (docked) size class — drives the expand-toggle icon. */
  isChatExpanded: boolean;
  onToggleExpand: () => void;
  /** Minimize to the FAB. */
  onClose: () => void;
  /** Controlled switcher-dropdown state (canvas); omit both for uncontrolled. */
  menuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  /** Surface-specific buttons between "new chat" and the size toggle (e.g. open-as-tab). */
  extraActions?: ReactNode;
}

export function FloatingChatHeader({
  title,
  updatedAt,
  sessions,
  currentSessionId,
  onSwitchSession,
  onDeleteSession,
  deleteSessionAriaLabel,
  onNewChat,
  newChatPending,
  newChatDisabled,
  canGoBack,
  onGoBack,
  isChatExpanded,
  onToggleExpand,
  onClose,
  menuOpen,
  onMenuOpenChange,
  extraActions,
}: FloatingChatHeaderProps) {
  return (
    // 44px INCLUDING the bottom hairline — the same box the detail-pane
    // headers use (CANVAS_DETAIL_PANE_HEADER_CLASS, !h-11 border-inside), so
    // the two hairlines meet at the same y wherever a docked or windowed chat
    // sits beside a pane. The old shape (border OUTSIDE an h-12 row = 49px)
    // left a visible 5px step at every seam.
    <div className="h-11 shrink-0 border-b border-n-10 bg-surface-floating-chat">
      <div className="flex h-full items-center justify-between gap-2.5 px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {canGoBack ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onGoBack}
                    className="shrink-0"
                    aria-label="Go back"
                  >
                    <ArrowLeft />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Back</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {/* Session selector — a down-arrow dropdown to switch chats / start a
              new one, available whenever the surface has at least one session. */}
          {sessions.length >= 1 ? (
            <DropdownMenu open={menuOpen} onOpenChange={onMenuOpenChange}>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    // Size to content (capped), not flex-1: the chevron sits
                    // right after the name/time and a gap remains before the
                    // right-hand button group. Long names truncate at the cap.
                    "-mx-1.5 flex min-w-0 max-w-[15rem] items-center gap-2 rounded-md px-1.5 py-1 text-left",
                    "outline-none transition-colors duration-150 ease-out hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust",
                  )}
                >
                  <span className="inline-flex size-[18px] shrink-0 items-center justify-center text-rust">
                    <MessagesSquare className="size-[18px]" />
                  </span>
                  <h2 className="truncate font-sans text-sm font-medium text-n-90">
                    {title}
                  </h2>
                  {updatedAt ? (
                    <span className="shrink-0 font-sans text-xs font-normal text-n-50">
                      {shortRelativeTime(updatedAt)}
                    </span>
                  ) : null}
                  <ChevronDown className="size-3.5 shrink-0 self-center text-n-40" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className={cn(
                  "w-80 p-0",
                  "bg-surface-floating-chat/95 backdrop-blur-lg",
                  "rounded-2xl border",
                  "shadow-2xl shadow-black/20",
                )}
              >
                <div className="max-h-[360px] overflow-y-auto p-2">
                  {sessions.map((session) => (
                    <div key={session.key} className="group relative">
                      <DropdownMenuItem
                        onClick={() => onSwitchSession(session.id)}
                        className={cn(
                          "flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 pr-10",
                          session.id === currentSessionId && "bg-accent",
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {session.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(session.updatedAt), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                        {session.id === currentSessionId && (
                          <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                        )}
                      </DropdownMenuItem>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSession(session.id);
                        }}
                        className={cn(
                          "absolute right-2 top-1/2 -translate-y-1/2",
                          "rounded-md p-1.5 opacity-0 group-hover:opacity-100",
                          "transition-all hover:bg-destructive/10",
                        )}
                        aria-label={deleteSessionAriaLabel}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t p-1.5">
                  <DropdownMenuItem
                    onClick={onNewChat}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    New chat
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="inline-flex size-[18px] shrink-0 items-center justify-center text-rust">
                <MessagesSquare className="size-[18px]" />
              </span>
              <h2 className="truncate font-sans text-sm font-medium text-n-90">
                {title}
              </h2>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {/* New session */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onNewChat}
                  disabled={newChatDisabled}
                  aria-label="Start new chat"
                >
                  {newChatPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Plus />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Start new chat</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {extraActions}

          {/* Size class: windowed ↔ full-height (docked). */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onToggleExpand}
                  aria-label={
                    isChatExpanded ? "Restore window" : "Expand to full height"
                  }
                >
                  {isChatExpanded ? <Minimize2 /> : <Maximize2 />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isChatExpanded ? "Restore window" : "Expand to full height"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Minimize to the FAB launcher. */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  aria-label="Minimize"
                >
                  <Minus />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Minimize</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
